import { Link } from "react-router-dom";
import { ArrowRight, Plane, ShieldCheck, TrendingUp, Users, Globe2, Award } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import AirZimbabweLogo from "@/components/AirZimbabweLogo";
import Seo from "@/components/Seo";
import { AIR_ZIMBABWE } from "@/lib/data";

const FLEET = "https://upload.wikimedia.org/wikipedia/commons/thumb/a/aa/Air_Zimbabwe_Boeing_767-200ER_Pichugin-1.jpg/1280px-Air_Zimbabwe_Boeing_767-200ER_Pichugin-1.jpg";

export default function AirlinePartnersPage() {
  return (
    <div data-testid="airline-partners-page">
      <Seo
        title="Airline Partners | Simba Travel — UK GSA for Air Zimbabwe"
        description="Simba Travel represents Air Zimbabwe across the United Kingdom and Ireland — and is built to onboard additional airline partners. Discover our GSA model, distribution reach and partnership framework."
      />
      <section className="relative h-[60vh] min-h-[420px] -mt-24 flex items-end overflow-hidden">
        <img src={FLEET} alt="Aircraft fleet" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight to-midnight/30" />
        <div className="relative container-edge pb-14 text-linen">
          <div className="overline text-ochre mb-4">Airline partners</div>
          <h1 className="editorial-h1 max-w-3xl">The carriers we represent in the United Kingdom.</h1>
        </div>
      </section>

      <section className="section-pad bg-linen">
        <div className="container-edge">
          <SectionHeading
            overline="Current partner"
            title="Air Zimbabwe."
            lede="Zimbabwe's national airline — represented by Simba Travel across the United Kingdom and Ireland."
          />

          <div className="mt-10 max-w-3xl">
            <p className="text-base leading-relaxed text-ink/80 font-light">
              Simba Travel is proud to be the appointed United Kingdom General Sales Agent (GSA) for Air Zimbabwe — the flag carrier of Zimbabwe and one of Southern Africa's most historic airlines. From our London base we deliver the full commercial footprint of Air Zimbabwe in the British market: sales representation, ticketing operations, refund handling, trade engagement, marketing, PR and dedicated call-centre support. Our role is to make it as easy for a UK travel agent, corporate booker or passenger to reach Harare, Bulawayo, Victoria Falls, Mutare or Dar es Salaam as it is to reach any European city — with a single UK-based team accountable end-to-end.
            </p>
          </div>

          <Reveal className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-12 items-center bg-sand p-8 md:p-14">
            <div className="md:col-span-5">
              <img
                src={FLEET}
                alt="Air Zimbabwe aircraft"
                className="w-full aspect-[4/3] object-contain bg-midnight"
              />
            </div>
            <div className="md:col-span-7">
              <div className="overline text-terracotta">Featured partner · UM</div>
              <AirZimbabweLogo className="h-12 w-auto my-4" variant="dark" />
              <h3 className="editorial-h2 mt-2">Air Zimbabwe</h3>
              <p className="text-muted2 mt-5 leading-relaxed">
                Founded in {AIR_ZIMBABWE.founded}, Air Zimbabwe is the flag carrier of Zimbabwe, operating from {AIR_ZIMBABWE.hub}. The airline connects Harare to a network of regional Southern and East African destinations.
              </p>
              <div className="mt-8 grid grid-cols-3 gap-6">
                <div>
                  <div className="font-serif text-3xl text-terracotta">{AIR_ZIMBABWE.founded}</div>
                  <div className="text-[10px] tracking-widest uppercase text-muted2 mt-1">Established</div>
                </div>
                <div>
                  <div className="font-serif text-3xl text-terracotta">{AIR_ZIMBABWE.fleet.reduce((a, f) => a + f.count, 0)}</div>
                  <div className="text-[10px] tracking-widest uppercase text-muted2 mt-1">Aircraft</div>
                </div>
                <div>
                  <div className="font-serif text-3xl text-terracotta">{AIR_ZIMBABWE.routes.length}+</div>
                  <div className="text-[10px] tracking-widest uppercase text-muted2 mt-1">Routes</div>
                </div>
              </div>
              <Link
                to="/airline-partners/air-zimbabwe"
                className="btn-primary mt-10"
                data-testid="airzim-profile-link"
              >
                View full Air Zimbabwe profile <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What we deliver */}
      <section className="section-pad bg-linen border-t border-ink/10" data-testid="gsa-deliverables">
        <div className="container-edge">
          <SectionHeading
            overline="Our GSA model"
            title="What we deliver for partner airlines."
            lede="A fully-integrated commercial infrastructure that behaves like your own UK office — without the fixed cost or complexity of setting one up."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            <Reveal>
              <ShieldCheck className="w-8 h-8 stroke-1 text-terracotta" />
              <h3 className="font-serif text-2xl mt-5">Revenue accountability</h3>
              <p className="text-sm text-muted2 mt-3 leading-relaxed">
                BSP-UK settlement, monthly financial reconciliation, transparent commission and cost reporting, plus quarterly business reviews with the airline's commercial leadership.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <Globe2 className="w-8 h-8 stroke-1 text-terracotta" />
              <h3 className="font-serif text-2xl mt-5">Distribution reach</h3>
              <p className="text-sm text-muted2 mt-3 leading-relaxed">
                Active in every major GDS (Amadeus, Sabre, Travelport), NDC channels, UK-focused OTAs and metasearch surfaces — plus direct relationships with UK's TMC consortia and specialist Africa tour operators.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Users className="w-8 h-8 stroke-1 text-terracotta" />
              <h3 className="font-serif text-2xl mt-5">Trade engagement</h3>
              <p className="text-sm text-muted2 mt-3 leading-relaxed">
                Named agency sales manager, ongoing trade training programmes, familiarisation trips, sales calls, industry event presence and co-operative marketing with UK travel media.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <TrendingUp className="w-8 h-8 stroke-1 text-terracotta" />
              <h3 className="font-serif text-2xl mt-5">Route development</h3>
              <p className="text-sm text-muted2 mt-3 leading-relaxed">
                Market-sizing studies, competitor benchmarking and fare-strategy input to help you optimise UK point-of-sale performance — whether you're launching a new route or scaling existing capacity.
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <Plane className="w-8 h-8 stroke-1 text-terracotta" />
              <h3 className="font-serif text-2xl mt-5">Ticketing & customer care</h3>
              <p className="text-sm text-muted2 mt-3 leading-relaxed">
                24-hour ticketing desk, dedicated refund team, GDS-trained reissue specialists and a bilingual UK-based customer service function — fully brandable to your airline.
              </p>
            </Reveal>
            <Reveal delay={0.5}>
              <Award className="w-8 h-8 stroke-1 text-terracotta" />
              <h3 className="font-serif text-2xl mt-5">Brand stewardship</h3>
              <p className="text-sm text-muted2 mt-3 leading-relaxed">
                Every UK-facing touchpoint — from press releases to trade collateral — reflects your brand standards and reinforces your positioning to British travellers and the wider travel trade.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Future partners */}
      <section className="section-pad bg-midnight text-linen">
        <div className="container-edge text-center">
          <Plane className="w-10 h-10 stroke-1 text-ochre mx-auto" />
          <h2 className="editorial-h2 text-linen mt-6 max-w-2xl mx-auto">
            Built to onboard additional airline partners.
          </h2>
          <p className="mt-6 text-linen/70 max-w-2xl mx-auto leading-relaxed">
            Our multi-airline content management, distribution infrastructure and brand-flexible contact centre are designed to onboard new carriers within 60 days of contract signature. Could your airline be next?
          </p>
          <Link to="/contact" className="btn-accent mt-10" data-testid="future-partner-cta">
            Discuss a partnership <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
